/**
 * Unified Analytics API
 * Объединяет все провайдеры и предоставляет единый интерфейс
 */

import { isDebugMode } from './config'
import { hasAnalyticsConsent, initConsent } from './consent'
import { ga4Provider } from './providers/ga'
import { ymProvider } from './providers/ym'
import { sanitizePII } from './sanitize'

import type {
  AddPaymentInfoParams,
  AddShippingInfoParams,
  AddToCartParams,
  AddToWishlistParams,
  AnalyticsProvider,
  ApplyPromoCodeParams,
  BeginCheckoutParams,
  LoginParams,
  PurchaseParams,
  RefundParams,
  RemoveFromCartParams,
  SearchParams,
  SelectItemParams,
  SignUpParams,
  ViewCartParams,
  ViewItemListParams,
  ViewItemParams,
} from './types'

// Список активных провайдеров
const providers: AnalyticsProvider[] = [ ga4Provider, ymProvider ]

// Флаг инициализации
let isInitialized = false

/**
 * Проверить, можно ли отправлять события аналитики
 */
function canSendEvents (): boolean {
  // Проверяем инициализацию
  if (!isInitialized) {
    if (isDebugMode()) {
      console.warn('[Analytics] Not initialized. Call analytics.init() first.')
    }
    return false
  }
  
  // Проверяем согласие пользователя
  if (!hasAnalyticsConsent()) {
    if (isDebugMode()) {
      console.log('[Analytics] Consent not granted, skipping event')
    }
    return false
  }
  
  return true
}

/**
 * Выполнить метод на всех доступных провайдерах
 */
function broadcast<T extends keyof AnalyticsProvider> (
  method: T,
  ...args: AnalyticsProvider[T] extends (...args: infer P) => unknown ? P : never
): void {
  // Пропускаем если нет согласия (кроме init)
  if (method !== 'init' && !canSendEvents()) {
    return
  }
  
  providers.forEach((provider) => {
    try {
      if (provider.isAvailable()) {
        const fn = provider[method]
        if (typeof fn === 'function') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(fn as (...args: any[]) => void).apply(provider, args)
        }
      }
    }
    catch (error) {
      console.error(`[Analytics] Error in ${provider.name}.${method}:`, error)
    }
  })
}

/**
 * Unified Analytics API
 */
export const analytics = {
  /**
   * Инициализация всех провайдеров
   */
  init (): void {
    if (isInitialized) {
      return
    }
    
    // Инициализируем consent-менеджер
    initConsent()
    
    providers.forEach((provider) => {
      try {
        provider.init()
      }
      catch (error) {
        console.error(`[Analytics] Error initializing ${provider.name}:`, error)
      }
    })
    
    isInitialized = true
    
    if (isDebugMode()) {
      console.log('[Analytics] Initialized')
    }
  },
  
  /**
   * Проверить, инициализирована ли аналитика
   */
  isInitialized (): boolean {
    return isInitialized
  },
  
  /**
   * Проверить, можно ли отправлять события
   */
  canTrack (): boolean {
    return canSendEvents()
  },

  /**
   * Получить список активных провайдеров
   */
  getActiveProviders (): string[] {
    return providers.filter((p) => p.isAvailable()).map((p) => p.name)
  },

  // ==========================================================================
  // Ecommerce события
  // ==========================================================================

  /**
   * Просмотр списка товаров (каталог, поиск, рекомендации)
   */
  viewItemList (params: ViewItemListParams): void {
    broadcast('viewItemList', params)
  },

  /**
   * Просмотр карточки товара
   */
  viewItem (params: ViewItemParams): void {
    broadcast('viewItem', params)
  },

  /**
   * Клик по товару в списке
   */
  selectItem (params: SelectItemParams): void {
    broadcast('selectItem', params)
  },

  /**
   * Добавление товара в корзину
   */
  addToCart (params: AddToCartParams): void {
    broadcast('addToCart', params)
  },

  /**
   * Удаление товара из корзины
   */
  removeFromCart (params: RemoveFromCartParams): void {
    broadcast('removeFromCart', params)
  },

  /**
   * Просмотр корзины
   */
  viewCart (params: ViewCartParams): void {
    broadcast('viewCart', params)
  },

  /**
   * Начало оформления заказа
   */
  beginCheckout (params: BeginCheckoutParams): void {
    broadcast('beginCheckout', params)
  },

  /**
   * Выбор способа доставки
   */
  addShippingInfo (params: AddShippingInfoParams): void {
    broadcast('addShippingInfo', params)
  },

  /**
   * Выбор способа оплаты
   */
  addPaymentInfo (params: AddPaymentInfoParams): void {
    broadcast('addPaymentInfo', params)
  },

  /**
   * Успешная покупка
   */
  purchase (params: PurchaseParams): void {
    broadcast('purchase', params)
  },

  /**
   * Возврат
   */
  refund (params: RefundParams): void {
    broadcast('refund', params)
  },

  // ==========================================================================
  // Кастомные события
  // ==========================================================================

  /**
   * Поиск
   */
  search (params: SearchParams): void {
    broadcast('search', params)
  },

  /**
   * Авторизация
   */
  login (params: LoginParams): void {
    broadcast('login', params)
  },

  /**
   * Регистрация
   */
  signUp (params: SignUpParams): void {
    broadcast('signUp', params)
  },

  /**
   * Добавление в избранное
   */
  addToWishlist (params: AddToWishlistParams): void {
    broadcast('addToWishlist', params)
  },

  /**
   * Применение промокода
   */
  applyPromoCode (params: ApplyPromoCodeParams): void {
    broadcast('applyPromoCode', params)
  },

  /**
   * Произвольное событие
   * Параметры автоматически санитизируются для защиты PII
   */
  trackEvent (eventName: string, params?: Record<string, unknown>): void {
    const sanitizedParams = params ? sanitizePII(params) : undefined
    broadcast('trackEvent', eventName, sanitizedParams)
  },

  // ==========================================================================
  // User Management
  // ==========================================================================

  /**
   * Установить ID пользователя
   * Рекомендуется использовать хешированный ID, а не реальные данные
   */
  setUserId (userId: string | null): void {
    broadcast('setUserId', userId)
  },

  /**
   * Установить свойства пользователя
   * Данные автоматически санитизируются для защиты PII
   */
  setUserProperties (properties: Record<string, unknown>): void {
    const sanitizedProperties = sanitizePII(properties)
    broadcast('setUserProperties', sanitizedProperties)
  },
}

export default analytics
